import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../supabaseClient';

// 跨日快取
let cachedUsers = null;
let cachedCompanyId = null;
let cacheDate = null;

function ensureCacheDate() {
  const today = new Date().toISOString().slice(0, 10);
  if (cacheDate !== today) {
    cachedUsers = null;
    cachedCompanyId = null;
    cacheDate = today;
  }
}

/**
 * useCompanyUsers — 載入公司全部人員（含 department_name）
 *
 * 遷移後直接查主系統 DB（profiles + departments）
 *
 * 回傳：
 *   users: [{ id, name, email, avatar_url, role, company_id, department_id, department_name }]
 *   usersMapByEmail: { [email]: userData } — 用於 client-side join
 *   loading: boolean
 */
const useCompanyUsers = () => {
  const { profile, viewingCompanyId } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const companyId = viewingCompanyId || profile?.company_id;
    if (!companyId) return;

    const load = async () => {
      // 檢查跨日快取
      ensureCacheDate();
      if (cachedUsers && cachedCompanyId === companyId) {
        setUsers(cachedUsers);
        return;
      }

      setLoading(true);
      try {
        // 直查 profiles 表（遷移後同 DB）
        // 邏輯對齊主系統 /api/users：approval_status=approved + 包含 super_admin
        const { data: profiles, error } = await supabase
          .from('profiles')
          .select('id, email, name, avatar_url, role, company_id, department_id')
          .eq('approval_status', 'approved')
          .eq('is_active', true)
          .or(`company_id.eq.${companyId},role.eq.super_admin`)
          .order('name');

        if (error) {
          console.error('Error fetching company users:', error);
          return;
        }

        // 載入 departments 做 client-side join
        const { data: departments } = await supabase
          .from('departments')
          .select('id, name')
          .eq('company_id', companyId);

        const deptMap = {};
        (departments || []).forEach(d => { deptMap[d.id] = d.name; });

        const result = (profiles || []).map(p => ({
          id: p.id,
          name: p.name || p.email?.split('@')[0] || p.email,
          email: p.email,
          avatar_url: p.avatar_url,
          role: p.role,
          company_id: p.company_id,
          department_id: p.department_id,
          department_name: deptMap[p.department_id] || '',
        }));

        cachedUsers = result;
        cachedCompanyId = companyId;
        setUsers(result);
      } catch (err) {
        console.error('Error fetching company users:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [viewingCompanyId, profile?.company_id]);

  // 以 email 為 key 的 map，方便 client-side join
  const usersMapByEmail = useMemo(() => {
    const map = {};
    for (const u of users) {
      if (u.email) map[u.email.toLowerCase()] = u;
    }
    return map;
  }, [users]);

  return { users, usersMapByEmail, loading };
};

export default useCompanyUsers;
